// src/components/Designation/DesignationTable.jsx
// Component to display designation data in a table format
import React from 'react';
import RoleBasedComponent from '../common/RoleBasedComponent';

/**
 * DesignationTable Component
 * Displays a list of designations in a table format with edit/delete actions
 *
 * @param {Array} designations - Array of designation objects to display
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const DesignationTable = ({ designations, onEdit, onDelete, isLoading }) => {
  /**
   * Handle edit button click
   * @param {Object} designation - The designation item to edit
   */
  const handleEdit = (designation) => {
    if (onEdit) {
      onEdit(designation);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} designation - The designation item to delete
   */
  const handleDelete = (designation) => {
    if (onDelete) {
      onDelete(designation);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Designation Name</th>
              <th>Active On/Off</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="4" className="inventory-loading">
                Loading designation data...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Show empty state
  if (!designations || designations.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Designation Name</th>
              <th>Active On/Off</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="4" className="inventory-empty-state">
                No designations match the current filter.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Designation Name</th>
            <th>Active On/Off</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {designations.map((designation) => (
            <tr key={designation.id || designation.designationId}>
              {/* ID (support both designationId and id) */}
              <td>{designation.designationId || designation.id}</td>

              {/* Designation Name */}
              <td>{designation.name}</td>

              {/* Active On/Off */}
             <td>
                <span className={`category-status-badge ${
                  designation.isActive ? 'category-status-active' : 'category-status-inactive'
                }`}>
                  {designation.isActive ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      Active
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Inactive
                    </>
                  )}
                </span>
              </td>

              {/* Actions */}
              <td>
                {/* ✅ Delete Button - Only visible to Super Admin */}
<RoleBasedComponent allowedRoles={['superadmin']}></RoleBasedComponent>
                <button
  className="inventory-btn inventory-btn-action inventory-btn-edit"
  onClick={() => handleEdit(designation)}
  title="Edit this designation"
>
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
  Edit
</button>

{/* ✅ Delete Button - Only visible to Super Admin */}
<RoleBasedComponent allowedRoles={['superadmin']}>
  <button
    className="inventory-btn inventory-btn-action inventory-btn-delete"
    onClick={() => handleDelete(designation)}
    title="Delete this designation"
    style={{ marginLeft: '8px' }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
    Delete
  </button>
</RoleBasedComponent>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DesignationTable;